import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import api from "@/services/api";
import { jwtDecode } from "jwt-decode";
import { FcGoogle } from "react-icons/fc";
import { FaGithub } from "react-icons/fa";
import toast from "react-hot-toast";
import { useMyContext } from "@/context/ContextApi";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const apiUrl = import.meta.env.VITE_API_URL;

const getErrorMessages = (t) => ({
  access_denied: t('signin_errorAccessDenied'),
  oauth_failed: t('signin_errorOAuthFailed'),
  oauth_unknown: t('signin_errorOAuthUnknown'),
  invalid_request: t('signin_errorInvalidRequest'),
  unsupported_provider: t('signin_errorUnsupportedProvider'),
  user_creation_failed: t('signin_errorUserCreationFailed'),
});

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const error = params.get("error");
    const twoFAtoken = params.get("twoFAtoken");

    const errorMessages = getErrorMessages(t);
    if (error && errorMessages[error]) {
      toast.error(errorMessages[error]);
      navigate("/login", { replace: true });
    }

    if (twoFAtoken) {
      setStep(2);
      setTwoFactorToken(twoFAtoken);
    }
  }, [location.search, navigate]);

  const [step, setStep] = useState(1);
  const [twoFactorToken, setTwoFactorToken] = useState("");
  const [loading, setLoading] = useState(false);
  const { setToken, token } = useMyContext();

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: { username: "", password: "", code: "" },
    mode: "onTouched",
  });

  const handleSuccessfulLogin = (token, decodedToken) => {
    localStorage.setItem("JWT_TOKEN", token);
    setToken(token);
    navigate("/playerhub");
  };

  const onLoginHandler = async (data) => {
    try {
      setLoading(true);
      const response = await api.post("/auth/public/signin", data);
      toast.success(t('signin_successMessage'));
      reset();
      if (response.status === 200 && (response.data.jwtToken || response.data.twoFactorRequired)) {
        if (response.data.twoFactorRequired) {
          setTwoFactorToken(response.data.twoFactorToken);
          setStep(2);
        } else {
          const decodedToken = jwtDecode(response.data.jwtToken);
          handleSuccessfulLogin(response.data.jwtToken, decodedToken);
        }
      } else {
        toast.error(t('signin_errorLoginFailed'));
      }
    } catch {
      toast.error(t('signin_errorInvalidCredentials'));
    } finally {
      setLoading(false);
    }
  };

  const onVerify2FaHandler = async (data) => {
    setLoading(true);
    try {
      const response = await api.post("/auth/public/verify-2fa-login", {
        code: data.code,
        twoFactorToken,
      });
      const decodedToken = jwtDecode(response.data.jwtToken);
      handleSuccessfulLogin(response.data.jwtToken, decodedToken);
    } catch {
      toast.error(t('signin_error2FAInvalid'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) navigate("/playerhub");
  }, [navigate, token]);

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">

        {step === 1 ? (
          <>
            {/* Header */}
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold text-white">{t('signin_title')}</h1>
              <p className="mt-1 text-sm text-zinc-500">{t('signin_subtitle')}</p>
            </div>

            <form onSubmit={handleSubmit(onLoginHandler)} className="space-y-4">
              {/* OAuth */}
              <div className="flex gap-2">
                <a href={`${apiUrl}/oauth2/authorization/google`} className="flex-1 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm text-zinc-300 transition-all duration-150">
                    <FcGoogle className="text-lg shrink-0" />
                    {t('signin_oauthGoogle')}
                </a>
                <a href={`${apiUrl}/oauth2/authorization/github`} className="flex-1 w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-sm text-zinc-300 transition-all duration-150">
                    <FaGithub className="text-lg shrink-0" />
                    {t('signin_oauthGithub')}
                </a>
              </div>

              <div className="flex items-center gap-3">
                <Separator className="flex-1 bg-white/8" />
                <span className="text-xs text-zinc-600">{t('signin_separator')}</span>
                <Separator className="flex-1 bg-white/8" />
              </div>

              {/* Username */}
              <div className="space-y-1.5">
                <Label htmlFor="username" className="text-zinc-400 text-xs uppercase tracking-wider">{t('signin_usernameLabel')}</Label>
                <Input
                  id="username"
                  placeholder={t('signin_usernamePlaceholder')}
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
                  {...register("username", { required: true })}
                />
                {errors.username && <p className="text-xs text-red-400">{t('signin_usernameRequired')}</p>}
              </div>

              {/* Password */}
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-zinc-400 text-xs uppercase tracking-wider">{t('signin_passwordLabel')}</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('signin_passwordPlaceholder')}
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50"
                  {...register("password", { required: true })}
                />
                {errors.password && <p className="text-xs text-red-400">{t('signin_passwordRequired')}</p>}
              </div>

              <Button
                disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold"
                type="submit"
              >
                {loading ? t('signin_submitButtonLoading') : t('signin_submitButton')}
              </Button>

              <div className="flex items-center justify-between text-xs text-zinc-500">
                <Link to="/forgot-password" className="hover:text-zinc-300 transition-colors">
                  {t('signin_forgotPassword')}
                </Link>
                <Link to="/signup" className="hover:text-zinc-300 transition-colors">
                  {t('signin_createAccount')}
                </Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="mb-8 text-center">
              <h1 className="text-2xl font-bold text-white">{t('signin_twoFactorTitle')}</h1>
              <p className="mt-1 text-sm text-zinc-500">{t('signin_twoFactorSubtitle')}</p>
            </div>

            <form onSubmit={handleSubmit(onVerify2FaHandler)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="code" className="text-zinc-400 text-xs uppercase tracking-wider">{t('signin_codeLabel')}</Label>
                <Input
                  id="code"
                  placeholder={t('signin_codePlaceholder')}
                  className="bg-white/5 border-white/10 text-white placeholder:text-zinc-600 focus-visible:ring-violet-500/50 focus-visible:border-violet-500/50 text-center text-lg tracking-widest"
                  {...register("code", { required: true })}
                />
                {errors.code && <p className="text-xs text-red-400">{t('signin_codeRequired')}</p>}
              </div>

              <Button
                disabled={loading}
                className="w-full bg-violet-600 hover:bg-violet-500 text-white font-semibold"
                type="submit"
              >
                {loading ? t('signin_verifyButtonLoading') : t('signin_verifyButton')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginPage;
